import React, { useEffect, useState } from "react";
import { Loader2, Search, X } from "lucide-react";
import { toast } from "sonner";
import { availityPayers, curatedPayers, patchInsurance } from "@/lib/noteClaim";
import { INK, SUB, FAINT, INDIGO, HAIR } from "./erShared";

// Inline payer search, prefilled with the carrier read from the document.
function InlineSearch({ carrier, clientId, onPick }) {
  const [q, setQ] = useState(carrier || "");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [curated, setCurated] = useState([]);
  useEffect(() => { let a = true; curatedPayers(clientId).then((c) => { if (a) setCurated(c); }).catch(() => {}); return () => { a = false; }; }, [clientId]);
  useEffect(() => {
    const term = q.trim(); if (term.length < 2) { setItems([]); return undefined; }
    setLoading(true);
    const t = setTimeout(async () => {
      let out = [];
      try { out = await availityPayers(term, 20); } catch { out = []; }
      if (!out.length) { const tl = term.toLowerCase(); out = curated.filter((p) => p.name.toLowerCase().includes(tl) || String(p.payer_id).includes(tl)).map((p) => ({ name: p.name, payer_id: p.payer_id })); }
      setItems(out); setLoading(false);
    }, 250);
    return () => clearTimeout(t);
  }, [q, curated]);
  return (
    <div className="w-full max-w-[460px]" data-testid="er-bulk-search">
      <div className="relative"><Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2" style={{ color: FAINT }} /><input autoFocus value={q} onChange={(e) => setQ(e.target.value)} placeholder={`Match a payer for all`} className="w-full text-[13px] pl-8 pr-2 h-9 rounded-[8px] outline-none" style={{ border: `1px solid ${INDIGO}`, boxShadow: "0 0 0 3px rgba(88,86,214,.14)" }} data-testid="er-bulk-input" /></div>
      <div className="mt-1 max-h-[220px] overflow-auto rounded-[8px]" style={{ border: items.length ? `1px solid ${HAIR}` : "none" }}>
        {loading ? <div className="px-2 py-2 text-[12px] inline-flex items-center gap-1.5" style={{ color: FAINT }}><Loader2 size={12} className="animate-spin" />Searching…</div>
          : items.map((p, i) => (
            <button key={i} type="button" onClick={() => onPick({ payer_name: p.name, payer_id: String(p.payer_id ?? p.id ?? "") })} className="w-full text-left px-2.5 py-1.5 text-[12.5px] hover:bg-[#F1F1F4] flex items-center justify-between" data-testid={`er-bulk-opt-${i}`}>
              <span className="truncate" style={{ color: INK }}>{p.name}</span><span className="font-mono text-[11px] ml-2" style={{ color: FAINT }}>{p.payer_id ?? p.id}</span>
            </button>
          ))}
      </div>
    </div>
  );
}

// Group header for the "Needs payer or payer id" view: carrier name + count + "Pick payer for all N".
export function BulkPayerHeader({ carrier, notes, clientId, onApplied }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const n = notes.length;
  const apply = async (payer) => {
    if (!payer.payer_id) { toast.error("Pick a payer with a payer id."); return; }
    setBusy(true);
    let ok = 0;
    for (const note of notes) {
      try {
        await patchInsurance(note.id, { payer_id: payer.payer_id, payer_name: payer.payer_name, member_id: note.insurance?.member_id || "", group_number: note.insurance?.group_number || "", sequence: 1, teach_alias: carrier });
        ok += 1;
      } catch { /* keep going */ }
    }
    setBusy(false); setOpen(false);
    toast.success(`${payer.payer_name} matched to ${ok} visit${ok === 1 ? "" : "s"} · revQ will match '${carrier}' next time`);
    onApplied?.();
  };
  return (
    <div className="flex items-center gap-3 px-4 py-2 flex-wrap" style={{ background: "#FBFBFD", borderBottom: `1px solid ${HAIR}` }} data-testid={`er-bulk-group-${carrier.replace(/\s+/g, "-").toLowerCase()}`}>
      <span className="text-[13px] font-bold" style={{ color: INK }}>{carrier}</span>
      <span className="text-[12px]" style={{ color: FAINT }}>{n} visit{n === 1 ? "" : "s"} · no payer match</span>
      <span className="flex-1" />
      {busy ? (
        <span className="text-[12.5px] inline-flex items-center gap-1.5" style={{ color: FAINT }}><Loader2 size={13} className="animate-spin" />Linking {n} visit{n === 1 ? "" : "s"}…</span>
      ) : open ? (
        <div className="flex items-start gap-2">
          <InlineSearch carrier={carrier} clientId={clientId} onPick={apply} />
          <button type="button" onClick={() => setOpen(false)} className="text-[12.5px] font-semibold inline-flex items-center gap-1 mt-1.5" style={{ color: SUB }} data-testid="er-bulk-cancel"><X size={13} />Cancel</button>
        </div>
      ) : (
        <button type="button" onClick={() => setOpen(true)} className="text-[12.5px] font-bold rounded-[8px] px-3 h-8" style={{ border: `1px solid ${HAIR}`, color: INK, background: "#fff" }} data-testid="er-bulk-pick">Match payer for all {n}</button>
      )}
    </div>
  );
}

// Group header for visits with no payer at all — no bulk action; each row keeps its own Add insurance.
export function NoPayerHeader({ notes }) {
  const n = notes.length;
  return (
    <div className="flex items-center gap-2 px-4 py-2" style={{ background: "#FBFBFD", borderBottom: `1px solid ${HAIR}` }} data-testid="er-bulk-group-no-payer">
      <span className="text-[13px] font-bold" style={{ color: INK }}>No insurance</span>
      <span className="text-[12px]" style={{ color: FAINT }}>{n} visit{n === 1 ? "" : "s"} · add each one's insurance</span>
    </div>
  );
}
